import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, RulesPanel, SpaceJoinForm, TopicMultiSelect, ExpertiseEditor, expertisePayload, toEditableExpertise } from "@/components/common";
import { toast } from "sonner";
import { Plus, Users, MessagesSquare, Copy, Building2, ListChecks, UserPlus, Trash2 } from "lucide-react";

const SLUG = "campo-largo-2027";

export default function SpacePage() {
  const params = useParams();
  const slug = params.slug || SLUG;
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [space, setSpace] = useState(null);
  const [debates, setDebates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filter, setFilter] = useState("All");
  const [show, setShow] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", topic_tags: "", policy_area: "", scope_level: "space", scope_value: "", round_duration_days: 7, start_mode: "now", start_at: "" });

  const load = () => {
    api.get(`/spaces/${slug}`).then((r) => { setSpace(r.data); api.get(`/debates?space_id=${r.data.id}`).then((d) => setDebates(d.data)); });
    api.get("/policy-areas").then((r) => setAreas(r.data.areas));
  };
  useEffect(() => { load(); }, [slug]);

  const joined = space && user?.space_ids?.includes(space.id);

  const [profilePrompt, setProfilePrompt] = useState(false);
  const [pInterests, setPInterests] = useState([]);
  const [pExpertise, setPExpertise] = useState([]);
  const [pBusy, setPBusy] = useState(false);
  useEffect(() => {
    if (joined && slug) api.get(`/spaces/${slug}/profile`).then((r) => {
      const empty = (r.data.topics_interest || []).length === 0 && (r.data.expertise || []).length === 0;
      if (empty) setProfilePrompt(true);
    }).catch(() => {});
  }, [joined, slug]);
  const savePrompt = async () => {
    setPBusy(true);
    try {
      await api.put(`/spaces/${slug}/profile`, { topics_interest: pInterests, expertise: expertisePayload(pExpertise) });
      toast.success(t("Your space interests are saved"));
      setProfilePrompt(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPBusy(false); }
  };

  const copyOrganiserInvite = async () => {
    try {
      const { data } = await api.post(`/spaces/${slug}/organiser-invite`);
      const link = `${window.location.origin}/invite/${slug}?org=${encodeURIComponent(data.token)}`;
      await navigator.clipboard?.writeText(link);
      toast.success(t("Organiser invite link copied"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSpace = async () => {
    if (!window.confirm(t("Delete this space? This permanently removes all of its debates and cannot be undone."))) return;
    try {
      await api.delete(`/spaces/${slug}`);
      await refresh();
      toast.success(t("Space deleted"));
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async (e) => {
    e.preventDefault();
    const start_at = form.start_mode === "schedule" && form.start_at ? new Date(form.start_at).toISOString() : null;
    try {
      await api.post("/debates", { ...form, topic_tags: form.topic_tags.split(",").map((x) => x.trim()).filter(Boolean), space_id: space.id, round_duration_days: parseInt(form.round_duration_days), start_at });
      toast.success(t("Debate created — eligible members invited")); setShow(false);
      setForm({ ...form, title: "", description: "", topic_tags: "", start_mode: "now", start_at: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const g = {};
    debates.filter((d) => filter === "All" || d.policy_area === filter).forEach((d) => { (g[d.policy_area] = g[d.policy_area] || []).push(d); });
    return g;
  }, [debates, filter]);

  const inviteLink = `${window.location.origin}/invite/${slug}`;

  if (!space) return <p className="text-muted-foreground">{t("Loading…")}</p>;

  const isOrganiser = !!user && ((space.organiser_user_ids || []).includes(user.id) || space.creator_id === user.id || ["admin", "moderator", "platform_owner"].includes(user.role));
  const canDelete = !!user && (space.creator_id === user.id || ["admin", "moderator", "platform_owner"].includes(user.role));
  const spaceTopics = (space.topics && space.topics.length) ? space.topics : areas;

  return (
    <div className="space-y-8" data-testid="space-page">
      <div className="border border-border bg-white overflow-hidden">
        <div className="h-28 bg-primary relative">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        </div>
        <div className="p-6 sm:p-8">
          <p className="overline mb-1">{t("Deliberative Space · Programme building")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{space.name}</h1>
          <p className="text-muted-foreground mt-2 max-w-3xl">{space.description}</p>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Users size={15} /> {t("{{count}} members", { count: space.member_count })}</span>
            <span className="flex items-center gap-1"><MessagesSquare size={15} /> {t("{{count}} debates", { count: space.debate_count })}</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            {!user && <Link to={`/register?invite=${slug}`} data-testid="space-register-join" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors">{t("Register to join & take part")}</Link>}
            {user && !joined && <button onClick={() => setShowJoin(!showJoin)} data-testid="space-join-btn" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors">{t("Join space")}</button>}
            {joined && <button onClick={() => { if (!show && spaceTopics[0]) setForm((f) => ({ ...f, policy_area: spaceTopics[0] })); setShow(!show); }} data-testid="space-new-debate-btn" className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> {t("New debate")}</button>}
            <button onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success(t("Invite link copied")); }} data-testid="space-copy-invite" className="flex items-center gap-1.5 border border-border px-4 py-2 rounded-sm text-sm font-semibold hover:bg-muted transition-colors"><Copy size={15} /> {t("Copy invite link")}</button>
            {isOrganiser && <button onClick={copyOrganiserInvite} data-testid="space-copy-organiser-invite" className="flex items-center gap-1.5 border border-secondary text-secondary px-4 py-2 rounded-sm text-sm font-semibold hover:bg-secondary hover:text-white transition-colors"><UserPlus size={15} /> {t("Invite an organiser")}</button>}
            {canDelete && <button onClick={deleteSpace} data-testid="space-delete-btn" className="flex items-center gap-1.5 border border-destructive text-destructive px-4 py-2 rounded-sm text-sm font-semibold hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /> {t("Delete space")}</button>}
          </div>
        </div>
      </div>

      {!joined && showJoin && (
        <div data-testid="space-join-panel">
          <p className="overline mb-2">{t("Join this deliberative space")}</p>
          <SpaceJoinForm slug={slug} topics={spaceTopics} onJoined={async () => { await refresh(); setShowJoin(false); load(); }} />
        </div>
      )}

      {show && (
        <form onSubmit={create} className="border border-border bg-white p-6 space-y-3" data-testid="debate-form">
          <input required placeholder={t("Debate question / title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ic} data-testid="debate-title" />
          <textarea required placeholder={t("Describe the issue")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={ic + " h-24"} data-testid="debate-desc" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={form.policy_area} onChange={(e) => setForm({ ...form, policy_area: e.target.value })} className={ic} data-testid="debate-policy-area">{spaceTopics.map((a) => <option key={a} value={a}>{t(a)}</option>)}</select>
            <input placeholder={t("Topic tags (comma)")} value={form.topic_tags} onChange={(e) => setForm({ ...form, topic_tags: e.target.value })} className={ic} />
            <select value={form.scope_level} onChange={(e) => setForm({ ...form, scope_level: e.target.value })} className={ic} data-testid="debate-scope">
              <option value="space">{t("This space (members vote)")}</option>
              <option value="city">{t("City")}</option><option value="region">{t("Region")}</option><option value="country">{t("Country")}</option><option value="eu">{t("European Union")}</option>
            </select>
            <select value={form.round_duration_days} onChange={(e) => setForm({ ...form, round_duration_days: e.target.value })} className={ic} data-testid="debate-duration">
              <option value={1}>{t("1 day / round")}</option><option value={3}>{t("{{count}} days / round", { count: 3 })}</option><option value={7}>{t("{{count}} days / round", { count: 7 })}</option><option value={14}>{t("{{count}} days / round", { count: 14 })}</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={form.start_mode} onChange={(e) => setForm({ ...form, start_mode: e.target.value })} className={ic} data-testid="debate-start-mode">
              <option value="now">{t("Start now (clock starts immediately)")}</option>
              <option value="schedule">{t("Schedule a start time")}</option>
            </select>
            {form.start_mode === "schedule" && <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className={ic} data-testid="debate-start-at" required />}
          </div>
          <button data-testid="debate-create-submit" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold">{t("Create debate")}</button>
        </form>
      )}

      <div>
        <div className="flex items-center gap-2 mb-1"><Building2 size={18} className="text-secondary" /><h2 className="font-display text-xl font-bold tracking-tight">{t("Organisers")}</h2></div>
        <p className="text-sm text-muted-foreground mb-3">{t("Approved deliberations from this space are delivered to its organisers, who decide whether to support them. This is the final step — space deliberations are not forwarded to legislative bodies.")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {space.parties.map((p) => (
            <Link key={p.id} to={`/party/${p.id}`} data-testid="space-party" className="border border-border bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all block">
              <Badge tone="purple">{t("Organiser")}</Badge>
              <h3 className="font-display font-bold tracking-tight mt-2">{p.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
            </Link>
          ))}
          {(space.organiser_users || []).map((u) => (
            <div key={u.id} className="border border-border bg-white p-5" data-testid="space-organiser-user">
              <Badge tone="purple">{t("Organiser")}</Badge>
              <h3 className="font-display font-bold tracking-tight mt-2">{u.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{u.user_type.replace("_", " ")}</p>
            </div>
          ))}
        </div>
      </div>

      <SpaceTopics slug={slug} topics={spaceTopics} custom={!!(space.topics && space.topics.length)} isOrganiser={isOrganiser} onSaved={load} />

      {joined && <MySpaceProfile slug={slug} topics={spaceTopics} />}

      <RulesPanel />

      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h2 className="font-display text-xl font-bold tracking-tight mr-2">{t("Programme chapters")}</h2>
          <button onClick={() => setFilter("All")} className={`text-xs px-3 py-1 rounded-sm border ${filter === "All" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{t("All")}</button>
          {spaceTopics.map((a) => <button key={a} onClick={() => setFilter(a)} data-testid={`filter-${a}`} className={`text-xs px-3 py-1 rounded-sm border transition-colors ${filter === a ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{t(a)}</button>)}
        </div>
        {Object.keys(grouped).length === 0 && <p className="text-sm text-muted-foreground">{t("No debates yet in this chapter.")}</p>}
        <div className="space-y-8">
          {Object.entries(grouped).map(([area, items]) => (
            <div key={area}>
              <div className="flex items-center gap-2 mb-3 border-b-2 border-foreground pb-2"><Badge tone="primary">{t(area)}</Badge><span className="text-xs text-muted-foreground">{t("{{count}} debates", { count: items.length })}</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((d) => (
                  <Link key={d.id} to={`/debates/${d.id}`} data-testid="debate-card" className="border border-border bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all block">
                    <div className="flex items-center gap-2 mb-2"><Badge tone="blue">{t(d.stage.replace("_", " "))}</Badge><Badge>{t("Round {{n}}", { n: d.current_round })}</Badge></div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{d.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><Users size={13} /> {t("{{count}} participants", { count: d.participant_count })}</span><span className="flex items-center gap-1"><MessagesSquare size={13} /> {t("{{count}} contributions", { count: d.argument_count })}</span></div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {profilePrompt && space && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="space-profile-prompt">
          <div className="bg-white rounded-sm max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto border-2 border-foreground">
            <p className="overline mb-1">{t("Welcome to the space")}</p>
            <h3 className="font-display text-xl font-bold tracking-tight mb-2">{space.name}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t("Select your topics of interest and expertise for this space, so we can invite you to the debates that match you. You can change these any time.")}</p>
            <div className="space-y-4">
              <div><label className="overline block mb-1">{t("Topics of interest in this space")}</label><TopicMultiSelect options={spaceTopics} value={pInterests} onChange={setPInterests} testid="prompt-space-topics" /></div>
              <ExpertiseEditor options={spaceTopics} items={pExpertise} setItems={setPExpertise} testid="prompt-space-expertise" />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={savePrompt} disabled={pBusy} data-testid="prompt-save-btn" className="bg-primary text-white px-5 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">{pBusy ? t("Saving…") : t("Save & continue")}</button>
              <button onClick={() => setProfilePrompt(false)} data-testid="prompt-skip-btn" className="border border-border px-4 py-2 rounded-sm text-sm font-semibold">{t("Skip for now")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function MySpaceProfile({ slug, topics }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [interests, setInterests] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadProfile = () => api.get(`/spaces/${slug}/profile`).then((r) => { setInterests(r.data.topics_interest || []); setExpertise(toEditableExpertise(r.data.expertise)); });
  useEffect(() => { loadProfile(); }, [slug]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/spaces/${slug}/profile`, { topics_interest: interests, expertise: expertisePayload(expertise) });
      toast.success(t("Your space profile updated"));
      setOpen(false); loadProfile();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-white p-6 rounded-sm" data-testid="my-space-profile">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-display text-lg font-bold tracking-tight">{t("My interests & expertise in this space")}</h2>
        {!open && <button onClick={() => setOpen(true)} data-testid="edit-space-profile-btn" className="text-xs font-semibold text-primary border border-primary px-3 py-1.5 rounded-sm hover:bg-primary hover:text-white transition-colors">{t("Edit")}</button>}
      </div>
      <p className="text-sm text-muted-foreground mb-3">{t("These determine which debates in this space you're invited to.")}</p>
      {open ? (
        <div className="space-y-4">
          <TopicMultiSelect options={topics} value={interests} onChange={setInterests} testid="my-space-topics" />
          <ExpertiseEditor options={topics} items={expertise} setItems={setExpertise} testid="my-space-expertise" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid="save-space-profile-btn" className="bg-primary text-white px-5 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">{busy ? t("Saving…") : t("Save changes")}</button>
            <button onClick={() => setOpen(false)} className="border border-border px-5 py-2 rounded-sm text-sm font-semibold hover:bg-muted">{t("Cancel")}</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {interests.map((tp) => <Badge key={tp} tone="primary">{t(tp)}</Badge>)}
          {expertise.map((e, i) => <Badge key={"e" + i} tone="green">{t(e.area)}</Badge>)}
          {interests.length === 0 && expertise.length === 0 && <p className="text-sm text-muted-foreground">{t("No interests selected yet.")}</p>}
        </div>
      )}
    </div>
  );
}

const ic = "w-full border border-border bg-background rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary";

function SpaceTopics({ slug, topics, custom, isOrganiser, onSaved }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const startEdit = () => { setDraft((custom ? topics : []).join(", ")); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      const list = draft.split(",").map((x) => x.trim()).filter(Boolean);
      await api.put(`/spaces/${slug}/topics`, { topics: list });
      toast.success(t("Topics updated"));
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-white p-6 rounded-sm" data-testid="space-topics">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><ListChecks size={18} className="text-secondary" /><h2 className="font-display text-lg font-bold tracking-tight">{t("Discussion topics")}</h2></div>
        {isOrganiser && !editing && <button onClick={startEdit} data-testid="edit-topics-btn" className="text-xs font-semibold text-primary border border-primary px-3 py-1.5 rounded-sm hover:bg-primary hover:text-white transition-colors">{t("Manage topics")}</button>}
      </div>
      <p className="text-sm text-muted-foreground mb-3">{isOrganiser ? t("As the organiser, you decide which topics can be debated in this space.") : t("Debates in this space are organised under these topics.")}</p>
      {editing ? (
        <div className="space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("Topic titles, comma separated (e.g. Housing, Climate, Transport)")} className={ic + " h-20"} data-testid="topics-input" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid="save-topics-btn" className="bg-primary text-white px-4 py-1.5 rounded-sm text-sm font-semibold disabled:opacity-60">{busy ? t("Saving…") : t("Save topics")}</button>
            <button onClick={() => setEditing(false)} className="border border-border px-4 py-1.5 rounded-sm text-sm font-semibold hover:bg-muted">{t("Cancel")}</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="topics-list">
          {topics.map((tp) => <Badge key={tp} tone="primary">{t(tp)}</Badge>)}
          {topics.length === 0 && <p className="text-sm text-muted-foreground">{t("No topics defined yet.")}</p>}
        </div>
      )}
    </div>
  );
}
